/**
 * Loader: "people arriving".
 * Thirty-two dots scatter across a black screen, each one a person. As the
 * counter climbs, they drift in and fall into a single line, and the words
 * "We build with people." assemble beneath them. At 100 the whole screen
 * lifts like a curtain to reveal the entry page. Same black, same easing
 * as the page-transition curtain, so the site has one consistent gesture.
 *
 * All per-frame work writes straight to the DOM (no React state), so it
 * stays smooth on phones.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { prefersReducedMotion } from "../lib/hooks";

const N = 32;
const WORDS = ["We", "build", "with", "people."];

const rng = (seed) => { let s = seed; return () => (s = (s * 16807) % 2147483647) / 2147483647; };

export default function Loader({ onLift, onDone }) {
  const numRef = useRef(null);
  const wordsRef = useRef(null);
  const dotsRef = useRef([]);
  const rootRef = useRef(null);
  const [out, setOut] = useState(false);
  const doneRef = useRef(false);

  const seeds = useMemo(() => {
    const r = rng(48271);
    return Array.from({ length: N }, () => ({ x: r(), y: r(), ph: r() * 6.28 }));
  }, []);

  const cbRef = useRef({ onLift, onDone });
  cbRef.current = { onLift, onDone };

  const finish = useCallback((delay = 0) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setTimeout(() => {
      setOut(true);
      cbRef.current.onLift && cbRef.current.onLift();
      setTimeout(() => cbRef.current.onDone && cbRef.current.onDone(), 1050);
    }, delay);
  }, []);

  useEffect(() => {
    const DUR = prefersReducedMotion() ? 700 : 3200;
    const start = performance.now();
    let raf = 0;
    const words = wordsRef.current ? Array.from(wordsRef.current.children) : [];

    const tick = (now) => {
      const t = Math.min(1, (now - start) / DUR);
      const p = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const w = window.innerWidth, h = window.innerHeight;
      const pad = Math.max(24, w * 0.05);
      const rowY = h * 0.34;

      if (numRef.current) numRef.current.textContent = String(Math.round(p * 100)).padStart(3, "0");

      dotsRef.current.forEach((el, i) => {
        if (!el) return;
        const local = Math.min(1, Math.max(0, (p - (i / N) * 0.28) / 0.72));
        const q = 1 - Math.pow(1 - local, 4);
        const sx = seeds[i].x * w, sy = h * 0.06 + seeds[i].y * h * 0.66;
        const ex = pad + (i / (N - 1)) * (w - pad * 2), ey = rowY;
        const wob = (1 - q) * 9;
        const x = sx + (ex - sx) * q + Math.sin(now / 700 + seeds[i].ph) * wob;
        const y = sy + (ey - sy) * q + Math.cos(now / 800 + seeds[i].ph) * wob;
        el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
        el.style.opacity = String(0.35 + q * 0.65);
      });

      const n = p > 0.8 ? 4 : p > 0.56 ? 3 : p > 0.3 ? 2 : p > 0.08 ? 1 : 0;
      words.forEach((el, i) => el.classList.toggle("on", i < n));

      if (t < 1) raf = requestAnimationFrame(tick);
      else { rootRef.current && rootRef.current.classList.add("is-done"); finish(650); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seeds, finish]);

  return (
    <div className={`ld${out ? " is-out" : ""}`} ref={rootRef} role="status" aria-label="Loading Digital Billions Group">
      <div className="ld-top">
        <span className="ld-brand">Digital Billions Group</span>
        <button type="button" className="ld-skip t-cap" onClick={() => finish(0)}>Skip</button>
      </div>

      <div className="ld-field" aria-hidden="true">
        {seeds.map((s, i) => <i key={i} className="ld-dot" ref={(el) => (dotsRef.current[i] = el)} style={{ animationDelay: `${i * 22}ms` }} />)}
      </div>

      <p className="ld-words" ref={wordsRef} aria-hidden="true">
        {WORDS.map((w) => <span key={w}>{w}&nbsp;</span>)}
      </p>

      <div className="ld-foot">
        <div className="ld-num-wrap"><span className="ld-num" ref={numRef}>000</span><span className="ld-pct">%</span></div>
        <span className="t-cap ld-note">Version 5 · 2026</span>
      </div>
    </div>
  );
}
